import { TenantConfigViewItem, ViewDefnState } from 'src/dao/tenantConfigClient';
import { LYTargetData } from 'src/pages/AssortmentStrategy/TargetSetting/Criteria/Criteria.types';
import { ColDef, ValueParserParams, ValueGetterParams, ValueFormatterParams } from '@ag-grid-community/core';
import { SubheaderDropdownProps } from '../Subheader/SubheaderDropdown';
import { SalesAdjustmentConfig } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/Editors/SalesAdjustmentEditor';
import { ScrollTo } from 'src/common-ui/components/DataGrid/DataGrid';
import { CollapsibleCompanion, SortOption } from 'src/common-ui/components/CompanionListView/CompanionListView';
import { ParamedCalc } from 'src/utils/LibraryUtils/MathUtils';
import { StyleEditConfigColumn } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection.types';
import { PrintProps } from 'src/components/higherOrder/Print/Print';
import { BasicItem, BasicPivotItem } from 'src/worker/pivotWorker.types';
import { AppThunkDispatch } from 'src/store';
import { ConfigurableGridGroupBySelection } from './ConfigurableGrid.slice';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { ClientDataApi, DataApi, WorklistConfigButtons } from 'src/services/configuration/codecs/confdefnView';
import { MassEditConfig } from '../MassEdit/MassEditv2';
import { z } from 'zod';
import { ConfigurableGridComponentProps } from 'src/services/configuration/codecs/confdefnComponentProps';
import { SelectorSubheaderDropdownProps } from './ConfigurableGrid.selectors';
import { ValidValuesCache } from 'src/services/validValuesCache';
import { CoarseEditPayload } from 'src/dao/pivotClient';
import { AdornmentType } from 'src/services/configuration/codecs/viewdefns/literals';
import {
  ConfigurableGridActions,
  ConfigurablePostAction,
  MassEditCoordinateMap,
} from 'src/services/configuration/codecs/viewdefns/general';
import { Scope } from 'src/types/Scope';
import { ViewDataState } from 'src/types/Domain';
import { Option } from '../Configure/ConfigureModal';
import { ParseResult } from '../Configure/Configure';
import { StyleDetailsPopoverProps } from 'src/components/AssortmentStyleDetailsPopover/AssortmentStyleDetailsPopover';
import { FabProps } from 'src/components/higherOrder/withFab';
import { WithRouterProps } from 'src/components/higherOrder/withRouter';
import { SortByDirection } from 'src/components/Subheader/Subheader.types';
import { FavoriteResponseItem } from 'src/components/Subheader/Favorites/Favorites.types';
import { GroupByPayload } from '../Subheader/Subheader.slice';
import { TopMembers } from 'src/services/Scope.client';
import { WithBreadCrumbProps } from '../higherOrder/withBreadcrumb';
import { refreshOn } from 'src/services/configuration/codecs/viewdefns/viewdefn';

export interface GridField {
  headerName?: string;
  colId?: string;
  pinned?: boolean;
  cellClass?: string | string[];
  renderer?: string;
  hide?: boolean;
  valueParser?: (params: ValueParserParams) => any | string;
  valueGetter?: ((params: ValueGetterParams) => any) | string;
  valueFormatter?: (params: ValueFormatterParams) => string  ;
  footerValueGetter?: ((params: ValueGetterParams) => any) | string;
  editable?: boolean;
  field?: string;
  aggFunc?: string;
  dataIndex?: string;
}

export enum AggOptions {
  Sum = 'sum',
  Min = 'min',
  Max = 'max',
  Count = 'count',
  Avg = 'avg',
  First = 'first',
  Last = 'last',
  TotalName = 'totalName',
  Eval = 'eval',
}
export interface ColumnConfig extends Omit<TenantConfigViewItem, 'pinned'> {
  pinned?: boolean;
  editable?: boolean;
  calculation?: string;
  inputType?: string;
  dataApi?: DataApi;
  configApi?: ClientDataApi;
  renderer?: string;
  hidden?: boolean;
  width?: number;
  children?: ColumnConfig[];
  headerName?: string;
  aggregator?: AggOptions;
  enableValue?: boolean;
}

export interface TrueColDef extends ColDef {
  renderer?: string;
  footerValueGetter?: () => string; // subject to extensive change. Lazy right now
}

export interface ValueProps {
  config: ColumnConfig[] | null;
  rowData: BasicItem[];
}

export interface FunctionProps {
  onSave: (rowData: LYTargetData[]) => void;
  reinitialize: () => void;
  setConfigurations?: (configurations: any) => void;
}

export interface Props extends ValueProps, FunctionProps {}

export interface ConfigurableGridColumnDef extends StyleEditConfigColumn {
  useMassEditUpdate: boolean; // uses new endpoint for updates (currently single datapoint update only)
  useColumnHeaderMassEditUpdate?: boolean;
  postArrayAsString?: boolean;
  width?: number;
  dataApiLifecycle?: ClientDataApi;
  dataApiStore?: ClientDataApi;
  lifecycleConfig?: ClientDataApi;
  storeConfig?: ClientDataApi;
  dependentsApi?: ClientDataApi;
  cascadeGroupSelection?: boolean;
  dependents?: string[];
  tooltipDataIndex?: string;
  headitorParams?: {
    editType: string;
    tooltip?: string;
  };
}

export interface ConfigurableGridDispatchProps extends Pick<CollapsibleCompanion, 'onToggleCompanion'> {
  dispatch: AppThunkDispatch;
  updateGroupBy: ActionCreatorWithPayload<GroupByPayload | number | null>;
  updateTopMemberSelection: ActionCreatorWithPayload<string>;
  onUpdateConfig: (config: any) => void;
  updateAssortmentPlan: () => void;
  onRefreshConfigurableGridData: () => void;
  onRefreshTopAttributes: () => void;
  submitPayload: (payload: CoarseEditPayload) => Promise<void>;
  updateConfigureSelections?(selections: Option[]): void;
  addSelectedItemsToCart(): void;
  toggleSelections(selections: BasicPivotItem | BasicPivotItem[]): void;
  syncGridData(redecoratedData: BasicPivotItem[]): void;
  createMfpScope(topMembers: TopMembers, workflow: string): Promise<unknown>;
  setPreviousView?(url: string): void;
}

export interface ConfigurableGridValueProps extends Pick<CollapsibleCompanion, 'isCompanionCollapsed'> {
  /** @param idProp this is used for linking components (f/e companion list and grid) */
  idProp: string;
  /** @param  leafIdProp is used for grid level row identification (for example, row selection) */
  leafIdProp: string;
  viewDefnState: ViewDefnState;
  dataLoaded: boolean;
  favoritesList: FavoriteResponseItem[];
  groupBySelection?: ConfigurableGridGroupBySelection;
  configureOptions?: ParseResult;
  configureSelections?: Option[];
  columnDefs: ConfigurableGridColumnDef[];
  clientActionHandlers?: ClientActionHandler;
  dependentCalcs: {
    [key: string]: ParamedCalc;
  };
  massEditConfig?: MassEditConfig;
  updateCoordinateMap?: MassEditCoordinateMap;
  onlySendCoordinateMap?: boolean;
  // TODO: these need to point to viewdefn validator type
  // and fix types that have been ignored trickling down
  configuratorViewDefn?: any;
  unmodifiedViewDefn?: any;
  gridRowHeight?: number;
  groupRowHeight?: number;
  defaultCompanionSortField?: string;
  companionSortOptions: SortOption[];
  groupByDropdownProps?: SelectorSubheaderDropdownProps;
  floorsetDropdownProps?: SelectorSubheaderDropdownProps;
  data: BasicPivotItem[];
  topAttributesData: BasicPivotItem | undefined;
  topAttributesRefresh: refreshOn;
  topAttributesDataState: ViewDataState;
  salesAdjustmentConfig?: SalesAdjustmentConfig;
  flowStatus: number[];
  search: string;
  showPublish?: boolean;
  hideUnpublish?: boolean;
  unpublishText?: string;
  adornments: AdornmentType[];
  publishText?: string;
  publishAttribute?: string;
  configurablePostAction: ConfigurablePostAction;
  scopeSelection: Scope;
  viewDataState: ViewDataState;
  showUndo: boolean;
  isWorklistActive: boolean;
  fab: FabProps;
  assortmentCartLink?: string;
  cartCount?: number;
  allowWorklistFunctionality?: boolean;
  enableCheckboxSelection?: boolean;
  actions?: z.infer<typeof ConfigurableGridActions>;
  showLookBackPeriod?: boolean;
  lookBackPeriod?: string;
  worklistConfigButtons?: WorklistConfigButtons;
  sideNavCollapsed: boolean;
}

// These remain a type because of the or in ConfigurableGridOwnProps
export type ConfigurableGridProps = ConfigurableGridOwnProps &
  ConfigurableGridDispatchProps &
  ConfigurableGridValueProps &
  StyleDetailsPopoverProps &
  WithRouterProps &
  WithBreadCrumbProps;

export interface ConfigurableGridOwnProps extends z.infer<typeof ConfigurableGridComponentProps>, PrintProps {}

export interface ConfigurableGridState {
  /** used to signal to MassEdit that the grid is ready */
  gridRdyCnt: number;
  selectedId?: string;
  gridScrollTo?: ScrollTo;
  companionScrollTo?: ScrollTo;
  companionSortDirection: SortByDirection;
  companionSortField?: string;
  activeStyleColor: string;
  subheaderDropdownConfig?: SubheaderDropdownProps[];
  favoritesList?: FavoriteResponseItem[];
  groupByIndexTemp?: number;
  isConfirmationModalOpen: boolean;
  isConfirmationModalLoading: boolean;
  actionType: 'publish' | 'unpublish' | undefined;
  validValuesCache: ValidValuesCache;
  configureIsOpen?: boolean;
  currentConfigureSelections?: Option[];
  actionPopupActive?: boolean;
  actionPopupInd?: number;
  isMfpScopeLoading: boolean;
}

export interface TemplateRendererParams {
  mask: string;
  maskParams: Record<string, string>;
  classByCalc?: Record<string, string>;
}
export interface ConfigurableGridConfigItem extends StyleEditConfigColumn {
  rendererParams?: TemplateRendererParams;
  width?: number;
  dataApiLifecycle?: ClientDataApi;
  dataApiStore?: ClientDataApi;
  lifecycleConfig?: ClientDataApi;
  storeConfig?: ClientDataApi;
  dependentsApi?: ClientDataApi;
  postArrayAsString?: boolean;
  cascadeGroupSelection?: boolean;
  tabIndex?: number;
  dependents?: string[];
  wrapText?: boolean;
  wrapHeaderText?: boolean;
  useColumnHeaderMassEditUpdate?: boolean;
  classes?: string[];
  refData?: ColDef['refData'];
  tooltipDataIndex?: string;
  aggRendererParams?: {
    comparisonDataIndex: string;
  };
  headitorParams?: {
    editType: string;
    tooltip?: string;
  };
}

export interface ClientActionHandler {
  [clientHandler: string]: string[]; // value is array of dataIndices that require clientHandler logic
}

export interface ClientActionHandlersConfig {
  [action: string]: ClientActionHandler;
}

/**
 * State required to be implemented by any popup editors that are enabled for aggregate cell edits
 * */
export interface ConfigurableGridAggCellEditorState {
  valueChosen: boolean;
}

export interface MassColumnUpdateParams {
  value: string;
  dataIndex: string;
  nodes: BasicPivotItem[];
}

export enum AsyncCellState {
  Idle = 'Idle',
  Processing = 'Processing',
  Redecorating = 'Redecorating',
}
